import {
  Alert,
  HeaderWithAlert,
  ItemPaymentSubscription,
  LoadingSkeleton,
  PaymentMethod,
  Select,
  TitleSection,
} from 'components';
import { IOptionSelect } from 'components/atoms/Select/Select';
import { UserDetails } from 'components/molecules/User-Details/User-Details';
import { usePaymentsBillingRender } from '../Payments-billing/hooks/Payments-billing-render.hook';
import { usePaymentsAndBilling } from '../Payments-billing/hooks/Payments-billing.hook';
import './Subscriptions.scss';
import { SubscriptionsList } from './components/SubscriptionsList';
import { useSubscriptions } from './hooks/Subscriptions.hook';
import { useState, useMemo } from 'react';

// Helper function to truncate stripe_subscription_id to 6 characters
const truncateSubscriptionId = (id: string): string => {
  if (!id) return '';
  return id.length > 6 ? id.substring(0, 6) : id;
};

export const Subscriptions = () => {
  const {
    steps,
    navigate,
    user,
    showAlertSuccess,
    setShowAlertSuccess,
    payments,
    subscriptions,
    loading: isLoadingSubscriptions,
  } = useSubscriptions();

  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string | number>('all');

  const subscriptionOptions = useMemo<IOptionSelect[]>(() => {
    return [
      { value: 'all', description: 'Todas las suscripciones' },
      ...subscriptions.map((sub) => {
        const truncatedId = truncateSubscriptionId(sub.id);
        const productTitle =
          sub.products.length === 1
            ? sub.products[0]
            : sub.products.length === 2
            ? `${sub.products[0]}, ${sub.products[1]}`
            : sub.products.length > 2
            ? `${sub.products[0]}, ${sub.products[1]} y más...`
            : `Suscripción ${truncatedId}`;
        return {
          value: sub.id,
          description:
            `${productTitle} (${truncatedId})` || `Suscripción ${truncatedId}`,
        };
      }),
    ];
  }, [subscriptions]);

  const filteredPayments = useMemo(() => {
    if (selectedSubscriptionId === 'all' || !payments?.length) {
      return payments;
    }
    // Filter by stripe_subscription_id (which is stored in payment.subscription_id)
    return payments.filter(
      (payment) => payment.subscription_id === selectedSubscriptionId
    );
  }, [payments, selectedSubscriptionId]);

  const shouldShowFilter = subscriptions.length > 1;

  const {
    infoPopUp,
    handlePopUp,
    isLoadingCards,
    userCards,
    showPopUp,
    showPopUpEditCardCredit,
    setShowModalEditPaymentMethod,
    showModalEditPaymentMethod,
    goToEditCardCredit,
    isLoadingEdit,
    isLoading,
    amountToRefund,
    deleteItemFromUser,
  } = usePaymentsAndBilling();

  const { renderUserCards } = usePaymentsBillingRender();

  return (
    <>
      {showAlertSuccess && (
        <Alert
          type="successful"
          onClick={() => {
            setShowAlertSuccess(false);
          }}
          title="¡Suscripción creada!"
          message="Ahora tienes una suscripción activa"
        />
      )}
      <HeaderWithAlert
        steps={steps}
        onClick={() => navigate('/settings')}
      ></HeaderWithAlert>
      <UserDetails name={`${user.name} ${user.lastname}`} email={user.email} />
      <main
        className="subscriptions__container container__generic"
        role="main"
        aria-label="Gestión de suscripciones"
      >
        <section
          className="subscriptions__container__section"
          aria-label="Lista de suscripciones"
        >
          <SubscriptionsList />
        </section>
        <section
          className="subscriptions__payment"
          aria-label="Métodos de pago"
        >
          <PaymentMethod
            isLoadingCards={isLoadingCards}
            userCards={userCards}
            renderUserCards={renderUserCards}
            infoPopUp={infoPopUp}
            showPopUp={showPopUp}
            handlePopUp={handlePopUp}
            showPopUpEditCardCredit={showPopUpEditCardCredit}
            showModalEditPaymentMethod={showModalEditPaymentMethod}
            setShowModalEditPaymentMethod={setShowModalEditPaymentMethod}
            goToEditCardCredit={goToEditCardCredit}
            isLoadingEdit={isLoadingEdit}
            amountToRefund={amountToRefund}
            deleteItemFromUser={deleteItemFromUser}
            isLoading={isLoading}
          />
        </section>
        <section className="subscriptions__payments" aria-label="Pagos">
          <TitleSection title="pagos">
            {shouldShowFilter && (
              <div className="subscriptions__payments__filter">
                <Select
                  name="subscription-filter"
                  id="subscription-filter"
                  placeholder="Filtrar por suscripción"
                  options={subscriptionOptions}
                  value={selectedSubscriptionId}
                  onClick={(value) => setSelectedSubscriptionId(value)}
                  onBlur={() => {
                    // No action needed on blur
                  }}
                  isTouched={false}
                />
              </div>
            )}
          </TitleSection>
          <div className="subscriptions__content">
            <div className="subscriptions__content__items">
              {isLoadingSubscriptions ? (
                <LoadingSkeleton count={2} />
              ) : !filteredPayments?.length ? (
                <span>No tienes pagos registrados</span>
              ) : (
                filteredPayments.map((payment) => (
                  <ItemPaymentSubscription key={payment.id} payment={payment} />
                ))
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};
